import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { SessionProvider } from "./auth/SessionProvider";
import { BuildStampGuard } from "./auth/BuildStampGuard";
import { portalRoutes } from "./routes/routeTable";
import { registerServiceWorker } from "./pwa/serviceWorker";
import { readRetryDelay, shouldRetryRead } from "./lib/queryRetry";
import "./styles.css";
// The boxes and the client's page vocabulary, global since the second build round of 2026-09-24,
// because the client's pages and the pages outside the shell are all built from them now.
import "./ui/boxes.css";
import "./ui/clientPage.css";

/**
 * `gcTime` is short and nothing is persisted. architecture.md section 4: TanStack Query data is
 * memory-only and cleared on sign-out, tenant loss, membership suspension and token failure, so no
 * cache adapter, no IndexedDB and no service-worker route may ever hold an authenticated response.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      // A read the gateway failed is asked twice more, and nothing else is. Until 2026-09-19 this
      // was `false`, and one 502 was an error page. `app/lib/queryRetry.ts` says what counts.
      retry: shouldRetryRead,
      retryDelay: readRetryDelay,
      refetchOnWindowFocus: true
    }
  }
});

/**
 * The route table itself moved to `app/routes/routeTable.tsx` on 2026-09-08, and this file kept only
 * the mounting.
 *
 * Importing this module starts the application, which is correct for the entry point and makes it
 * unimportable from a test. The routes had to be testable: the router gained a not-found page and a
 * root error boundary, and both of those are properties of the table rather than of any component in
 * it. `tests/route-fallbacks.test.tsx` mounts the real table in a memory router for exactly that.
 */
const router = createBrowserRouter(portalRoutes);

registerServiceWorker();

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BuildStampGuard>
        <SessionProvider>
          <RouterProvider router={router} />
        </SessionProvider>
      </BuildStampGuard>
    </QueryClientProvider>
  </React.StrictMode>
);
